import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
    Badge,
    Button,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    MessageBar,
    MessageBarActions,
    MessageBarBody,
    MessageBarTitle,
    OverlayDrawer,
    Spinner,
    Switch,
} from '@fluentui/react-components';
import {
    ArrowClockwise20Regular,
    Dismiss24Regular,
    Info20Regular,
    Sparkle20Regular,
} from '@fluentui/react-icons';
import moment from 'moment';

import * as ComponentFunctions from './ConsumptionSyncProcessIAAnalysis.Functions';
import SyncAnalysisFindingCard from './SyncAnalysisFinding.Card';
import SyncAnalysisSummaryComponent from './SyncAnalysisSummary.Component';
import { IAAgentAvatar } from '../../../../commons/component/IAAgentAvatar/IAAgentAvatar.Component';
import { SyncAnalysisFinding, ISyncAnalysisFindingsFilters } from '../../../../models/Entities/Protocols/SyncAnalysis';
import { SyncAnalysisFindingTypeEnum } from '../../../../models/Enums/SyncAnalysisFindingTypeEnum';
import { SyncAnalysisStatusEnum } from '../../../../models/Enums/SyncAnalysisStatusEnum';
import { PaginationControlComponent } from '../../../../commons/component/PaginationControl/PaginationControl.Component';
import { SYNC_ANALYSIS_FINDINGS_DEFAULT_PAGE_SIZE } from '../../../../redux/middleware/ProtocolConsumptionSyncAnalysisMiddleware';

import './ConsumptionSyncProcessIAAnalysis.Styles.css';

const ConsumptionSyncProcessIAAnalysisComponent = (props: ComponentFunctions.Props) => {
    const { analysis } = props;

    // El servidor pagina y filtra; filtrar acá sólo alcanzaría a la página
    // cargada y dejaría mal los totales y el recorrido de páginas.
    const [selectedType, setSelectedType] = useState<SyncAnalysisFindingTypeEnum | undefined>(undefined);
    const [onlyNew, setOnlyNew] = useState<boolean>(false);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(SYNC_ANALYSIS_FINDINGS_DEFAULT_PAGE_SIZE);

    const filters: ISyncAnalysisFindingsFilters = useMemo(
        () => ({ findingType: selectedType, onlyNew: onlyNew || undefined }),
        [selectedType, onlyNew]
    );

    // Cambiar un filtro vuelve a la primera página: quedarse en la 7 de un
    // listado que ahora tiene 2 mostraría una página vacía.
    useEffect(() => {
        setPageNumber(1);
    }, [selectedType, onlyNew, pageSize]);

    useEffect(() => {
        if (props.isOpen && props.SyncProcessId) {
            props.GetProtocolConsumptionSyncAnalysis(props.SyncProcessId);
        }
    }, [props.isOpen, props.SyncProcessId]);

    // Los hallazgos se piden recién cuando hay un análisis terminado que los tenga.
    const analysisId = analysis?.id;
    const canListFindings = ComponentFunctions.hasFindingsToShow(analysis);

    useEffect(() => {
        if (props.isOpen && analysisId && canListFindings) {
            props.GetProtocolConsumptionSyncAnalysisFindings(analysisId, pageNumber, pageSize, filters);
        }
    }, [props.isOpen, analysisId, canListFindings, pageNumber, pageSize, filters]);

    const onRefresh = () => {
        props.GetProtocolConsumptionSyncAnalysis(props.SyncProcessId);
        if (analysisId && canListFindings) {
            props.GetProtocolConsumptionSyncAnalysisFindings(analysisId, pageNumber, pageSize, filters);
        }
    };

    // El análisis a demanda reemplaza al de la corrida: los filtros y la página
    // que estaban puestos son de hallazgos que ya no existen.
    const onExecute = () => {
        setSelectedType(undefined);
        setOnlyNew(false);
        setPageNumber(1);
        props.ExecuteProtocolConsumptionSyncAnalysis(props.SyncProcessId);
    };

    const statusVariant = ComponentFunctions.getStatusVariant(analysis?.status);
    const typeCounts = useMemo(() => ComponentFunctions.buildFindingTypeCounts(analysis), [analysis]);
    const totalFindings = useMemo(() => ComponentFunctions.getTotalFindingCount(analysis), [analysis]);

    const executedAt = analysis?.executedAt
        ? moment(analysis.executedAt).format('DD/MM/YYYY HH:mm')
        : undefined;

    const isFirstLoad = props.isGettingAnalysis && analysis === undefined;
    const notAnalysed = !props.isGettingAnalysis && analysis === null;

    // El análisis corre del lado del servidor: mientras el POST espera respuesta, y
    // también si lo disparó otro, el análisis queda persistido en curso.
    const analysisIsRunning = ComponentFunctions.isAnalysisRunning(analysis);
    const isRunning = props.isExecutingAnalysis || analysisIsRunning;

    // ── Espera del análisis automático ──
    // Al terminar bien, la corrida publica sola el pedido del análisis, y esa fila
    // tarda unos segundos en aparecer. Antes de ofrecer el disparo manual se espera
    // un minuto mirando: si el automático llega, el botón no hace falta.
    const hasNoAnalysis = analysis === null;
    const [graceElapsed, setGraceElapsed] = useState<boolean>(false);
    const awaitingAutomatic = props.SyncProcessFinished && hasNoAnalysis && !graceElapsed;

    // Otra corrida es otra espera: el minuto de la anterior no dice nada de ésta.
    useEffect(() => {
        setGraceElapsed(false);
    }, [props.SyncProcessId]);

    // Sin `isOpen` entre las dependencias a propósito: cerrar y volver a abrir el
    // panel no tiene por qué reiniciar la espera.
    useEffect(() => {
        if (!awaitingAutomatic) return;

        const timer = window.setTimeout(
            () => setGraceElapsed(true),
            ComponentFunctions.SYNC_ANALYSIS_AUTOMATIC_GRACE_MS
        );

        return () => window.clearTimeout(timer);
    }, [awaitingAutomatic]);

    // ── Sondeo ──
    // El servidor no avisa nada: el panel relee mientras el análisis corre y
    // también durante la espera, que es lo que hace que el automático aparezca
    // solo en vez de obligar a refrescar a mano.
    const shouldWatch = isRunning || awaitingAutomatic;
    const pollCount = useRef<number>(0);
    const [pollingGaveUp, setPollingGaveUp] = useState<boolean>(false);

    // El tope se cuenta por espera, no por vida del panel: cada vez que hay algo
    // nuevo que mirar vuelve a tener sus diez minutos.
    useEffect(() => {
        if (!shouldWatch) {
            pollCount.current = 0;
            setPollingGaveUp(false);
        }
    }, [shouldWatch]);

    useEffect(() => {
        if (!props.isOpen || !shouldWatch || pollingGaveUp) return;

        const timer = window.setInterval(() => {
            if (pollCount.current >= ComponentFunctions.SYNC_ANALYSIS_MAX_POLLS) {
                // Un análisis que quedó marcado en curso porque el proceso murió no
                // se despierta nunca: se corta y se le avisa al usuario.
                setPollingGaveUp(true);
                return;
            }

            pollCount.current += 1;
            props.RefreshProtocolConsumptionSyncAnalysis(props.SyncProcessId);
        }, ComponentFunctions.SYNC_ANALYSIS_POLL_INTERVAL_MS);

        return () => window.clearInterval(timer);
    }, [props.isOpen, props.SyncProcessId, shouldWatch, pollingGaveUp]);

    // El disparo manual es el respaldo del automático, no una forma de rehacer el
    // análisis: se ofrece sólo sobre una corrida terminada que nunca se analizó, y
    // recién cuando el automático tuvo su minuto para aparecer y no apareció.
    const canExecute =
        !props.isExecutingAnalysis && props.SyncProcessFinished && hasNoAnalysis && graceElapsed;

    return (
        <OverlayDrawer
            position="end"
            size="medium"
            open={props.isOpen}
            onOpenChange={(_, data) => {
                if (!data.open) props.onDismiss();
            }}
        >
            <DrawerHeader className="csa-header">
                <DrawerHeaderTitle
                    action={
                        <div className="csa-header__actions">
                            <Button
                                appearance="subtle"
                                icon={<ArrowClockwise20Regular />}
                                aria-label="Actualizar"
                                title="Actualizar"
                                // Sigue disponible durante la corrida: es un GET, y es la
                                // salida si el sondeo se dio por vencido.
                                disabled={props.isGettingAnalysis}
                                onClick={onRefresh}
                            />
                            <Button
                                appearance="subtle"
                                icon={<Dismiss24Regular />}
                                aria-label="Cerrar"
                                onClick={props.onDismiss}
                            />
                        </div>
                    }
                >
                    <span className="csa-header__title">
                        <IAAgentAvatar size={78} className="csa-header__titleAvatar" />
                        <span className="csa-header__titleText">
                            Análisis de la sincronización
                            <Badge appearance="filled" color="warning" size="small">PREVIEW</Badge>
                        </span>
                    </span>
                </DrawerHeaderTitle>
                <span className="csa-header__subtitle">
                    Revisión automática de la corrida: qué quedó sin liquidar y por qué.
                </span>
            </DrawerHeader>

            <DrawerBody className="csa-body">

                {isFirstLoad && (
                    <div className="csa-loading">
                        <Spinner size="tiny" />
                        <span>Buscando el análisis de esta corrida...</span>
                    </div>
                )}

                {/* El análisis corre del lado del servidor y puede demorar minutos: sin
                    este aviso, el panel parecería colgado. */}
                {isRunning && (
                    <div className="csa-running">
                        {!pollingGaveUp && <Spinner size="tiny" />}
                        <div className="csa-running__text">
                            <span className="csa-running__title">
                                {pollingGaveUp
                                    ? 'El análisis figura en curso'
                                    : 'Analizando la corrida...'}
                            </span>
                            <span className="csa-running__note">
                                {pollingGaveUp
                                    ? 'Sigue así después de un buen rato, así que el proceso pudo haber quedado interrumpido. El panel dejó de actualizarse solo: probá con Actualizar, o volvé a pedir el análisis.'
                                    : 'Revisa las visitas y la parametrización paciente por paciente. El panel se actualiza solo hasta que termine.'}
                            </span>
                        </div>
                    </div>
                )}

                {/* Que la corrida no tenga análisis es un estado normal, no un error:
                    es, además, el único lugar desde donde se lo pide. Qué se ofrece
                    acá depende de si el análisis automático todavía puede llegar. */}
                {notAnalysed && !props.failOnGettingAnalysis && !isRunning && (
                    <MessageBar intent="info">
                        <MessageBarBody>
                            <MessageBarTitle>Sin análisis</MessageBarTitle>
                            {!props.SyncProcessFinished
                                ? 'Esta corrida todavía no fue analizada. El análisis se pide solo cuando la sincronización termina.'
                                : awaitingAutomatic
                                    ? 'La sincronización pide el análisis sola al terminar. Esperando a que aparezca...'
                                    : 'Esta corrida terminó y no quedó analizada. Podés pedir el análisis a mano.'}
                        </MessageBarBody>
                        {canExecute && (
                            <MessageBarActions>
                                <Button
                                    appearance="primary"
                                    size="small"
                                    icon={<Sparkle20Regular />}
                                    onClick={onExecute}
                                >
                                    Analizar ahora
                                </Button>
                            </MessageBarActions>
                        )}
                    </MessageBar>
                )}

                {/* La ejecución falla por motivos que el análisis persistido no cuenta
                    —no está habilitada, ya hay una en curso—, así que se muestran aparte.
                    Se muestra SIEMPRE: el rechazo más común es justamente "ya hay un
                    análisis en curso", y esconderlo mientras algo figura en curso dejaba
                    la pantalla girando sin decir nunca por qué. */}
                {props.failOnExecutingAnalysis && (
                    <MessageBar intent="error">
                        <MessageBarBody>
                            <MessageBarTitle>No se pudo ejecutar el análisis</MessageBarTitle>
                            {props.executionError?.ErrorMessage}
                        </MessageBarBody>
                    </MessageBar>
                )}

                {props.failOnGettingAnalysis && (
                    <MessageBar intent="error">
                        <MessageBarBody>
                            <MessageBarTitle>No se pudo obtener el análisis</MessageBarTitle>
                            {props.error?.ErrorMessage}
                        </MessageBarBody>
                    </MessageBar>
                )}

                {analysis && (
                    <>
                        {/* ── Estado ── */}
                        <section className={'csa-status csa-status--' + statusVariant}>
                            <div className="csa-status__row">
                                <span className={'csa-status__pill csa-status__pill--' + statusVariant}>
                                    {ComponentFunctions.getStatusLabel(analysis.status)}
                                </span>
                                {executedAt && <span className="csa-status__date">{executedAt}</span>}
                                {analysis.requestedBy && (
                                    <span className="csa-status__by">por {analysis.requestedBy}</span>
                                )}
                            </div>

                            {/* No haberse ejecutado por falta de indicios es una decisión
                                deliberada del proceso: se explica para que no se lea
                                como una falla ni como un análisis vacío. */}
                            {analysis.status === SyncAnalysisStatusEnum.SkippedNoIndicators && (
                                <p className="csa-status__note">
                                    <Info20Regular />
                                    No se ejecutó porque la verificación previa no encontró indicios de
                                    liquidación faltante. La corrida quedó registrada igual.
                                </p>
                            )}

                            {analysis.status === SyncAnalysisStatusEnum.Failed && analysis.errorMessage && (
                                <p className="csa-status__error">{analysis.errorMessage}</p>
                            )}
                        </section>

                        {/* ── Resumen ── */}
                        {analysis.summary && (
                            <section className="csa-summary">
                                <span className="csa-summary__title">Resumen</span>
                                <SyncAnalysisSummaryComponent summary={analysis.summary} />
                            </section>
                        )}

                        {/* ── Métricas ──
                            Mientras el análisis corre, los contadores todavía están en
                            cero: se ocultan porque una fila de ceros se lee como un
                            resultado —"no encontró nada"— y no lo es. */}
                        {!analysisIsRunning && (
                            <section className="csa-metrics">
                                <div className="csa-metric">
                                    <span className="csa-metric__label">Evidencia analizada</span>
                                    <span className="csa-metric__value">{analysis.evidenceItemCount}</span>
                                </div>
                                <div className="csa-metric">
                                    <span className="csa-metric__label">Hallazgos</span>
                                    <span className="csa-metric__value">{totalFindings}</span>
                                </div>
                                <div className="csa-metric">
                                    <span className="csa-metric__label">Pendientes</span>
                                    <span className="csa-metric__value">{analysis.pendingFindingCount}</span>
                                </div>
                                {/* Sólo si hay: con la resolución de hallazgos sin usar, un
                                    cero permanente es ruido en la fila. */}
                                {analysis.discardedFindingCount > 0 && (
                                    <div className="csa-metric">
                                        <span className="csa-metric__label">Descartados</span>
                                        <span className="csa-metric__value">{analysis.discardedFindingCount}</span>
                                    </div>
                                )}
                            </section>
                        )}

                        {/* ── Hallazgos ── */}
                        {canListFindings && (
                            <section className="csa-findings">
                                <header className="csa-findings__head">
                                    <span className="csa-findings__title">Hallazgos</span>
                                    <Switch
                                        checked={onlyNew}
                                        onChange={(_, data) => setOnlyNew(!!data.checked)}
                                        label="Sólo nuevos"
                                    />
                                </header>

                                {/* Los contadores por tipo son a la vez el desglose y el
                                    filtro: son las mismas cifras que se querrían tocar. */}
                                {typeCounts.length > 0 && (
                                    <div className="csa-typeFilters" role="group" aria-label="Filtrar por tipo">
                                        <button
                                            type="button"
                                            className={
                                                'csa-typeChip' + (selectedType === undefined ? ' is-active' : '')
                                            }
                                            onClick={() => setSelectedType(undefined)}
                                        >
                                            Todos
                                            <span className="csa-typeChip__count">{totalFindings}</span>
                                        </button>
                                        {typeCounts.map(({ descriptor, count }) => (
                                            <button
                                                key={descriptor.type}
                                                type="button"
                                                title={descriptor.description + ' Ancla en: ' + descriptor.anchor + '.'}
                                                className={
                                                    'csa-typeChip csa-typeChip--type' + descriptor.type +
                                                    (selectedType === descriptor.type ? ' is-active' : '')
                                                }
                                                onClick={() =>
                                                    setSelectedType(
                                                        selectedType === descriptor.type ? undefined : descriptor.type
                                                    )
                                                }
                                            >
                                                {descriptor.label}
                                                <span className="csa-typeChip__count">{count}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {props.failOnGettingFindings && (
                                    <MessageBar intent="error">
                                        <MessageBarBody>
                                            <MessageBarTitle>No se pudieron obtener los hallazgos</MessageBarTitle>
                                            {props.error?.ErrorMessage}
                                        </MessageBarBody>
                                    </MessageBar>
                                )}

                                {props.isGettingFindings && props.findings.length === 0 && (
                                    <div className="csa-loading">
                                        <Spinner size="extra-tiny" />
                                        <span>Cargando hallazgos...</span>
                                    </div>
                                )}

                                {!props.isGettingFindings && props.findings.length === 0 && (
                                    <p className="csa-findings__empty">
                                        {selectedType !== undefined || onlyNew
                                            ? 'Ningún hallazgo coincide con el filtro.'
                                            : 'El análisis no dejó hallazgos.'}
                                    </p>
                                )}

                                {props.findings.length > 0 && (
                                    <ul className="csa-findings__list">
                                        {props.findings.map((finding: SyncAnalysisFinding) => (
                                            <SyncAnalysisFindingCard key={finding.id} finding={finding} />
                                        ))}
                                    </ul>
                                )}

                                <PaginationControlComponent
                                    paginationState={props.findingsPagination}
                                    showedRecords={props.findings.length}
                                    isLoading={props.isGettingFindings}
                                    setPageNumber={setPageNumber}
                                    setPageSize={setPageSize}
                                />
                            </section>
                        )}
                    </>
                )}
            </DrawerBody>
        </OverlayDrawer>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ConsumptionSyncProcessIAAnalysisComponent as any) as React.FC<ComponentFunctions.IProps>;
